import React, { useEffect, useMemo, useState, useRef, useContext } from "react";
import {
  Plus,
  Pencil,
  Check,
  X,
  Copy,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  Download,
  Upload,
  Search,
  Filter,
  Trash2,
} from "lucide-react";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { AuthContext } from "../context/AuthContext";

import {
  getRMRows,
  createRMRow,
  updateRMRow,
  bulkSaveRMRows,
  deleteRMRow,
} from "../api/Rmdatabase";

const CATEGORIES = [
  "Spices Bulk VRAC",
  "Spice Blends VRAC",
  "Spice Blends Custom VRAC",
];



const COLUMNS = [
  {
    key: "RMName",
    label: "RM Name",
    type: "text",
    headerAlign: "text-left",
  },
  {
    key: "RMCode",
    label: "RM Code",
    type: "number",
    headerAlign: "text-left",
  },
  {
    key: "RMCategory",
    label: "RM Category",
    type: "text",
    headerAlign: "text-left",
  },
  {
    key: "categoryPremiumKgCad",
    label: "Category Premium",
    type: "number",
    headerAlign: "text-right",
  },
  {
    key: "allergenPremiumKgCad",
    label: "Allergen Premium",
    type: "number",
    headerAlign: "text-right",
  },
  {
    key: "baseCostKgCad",
    label: "Base Cost / KG",
    type: "number",
    headerAlign: "text-right",
  },
];
const CATEGORY_PREMIUM_MAP = {
  "Spices Bulk VRAC": {
    categoryPremiumKgCad: 0,
    allergenPremiumKgCad: 0,
  },

  "Spice Blends VRAC": {
    categoryPremiumKgCad: 0.75,
    allergenPremiumKgCad: 0,
  },

  "Spice Blends Custom VRAC": {
    categoryPremiumKgCad: 1.25,
    allergenPremiumKgCad: 0,
  },
};


// {
//   key: "TotalRMCost",
//   label: "Total RM Cost",
//   type: "number",
// },
const getPremiumsByCategory = (category) => {
  const normalizedCategory = String(category || "").trim();

  return (
    CATEGORY_PREMIUM_MAP[normalizedCategory] || {
      categoryPremiumKgCad: 0,
      allergenPremiumKgCad: 0,
    }
  );
};

const fmt = (val, type, key) => {
  if (key === "RMCode") {
    return val;
  }

  if (type === "number") {
    return Number(val || 0).toFixed(2);
  }

  return val || "-";
};

function InlineInput({ value, onChange, onPaste, type = "text" }) {
  return (
    <input
      type={type === "number" ? "number" : "text"}
      step={type === "number" ? "0.01" : undefined}
      value={value ?? ""}
      onChange={(e) =>
        onChange(
          type === "number" ? Number(e.target.value || 0) : e.target.value
        )
      }
      onPaste={onPaste}
      className={`w-full px-2 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black bg-white ${type === "number" ? "font-mono text-right" : "font-sans"
        }`}
    />
  );
}

export default function RM_DB() {
  const { user } = useContext(AuthContext) || {};
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const [editingRows, setEditingRows] = useState(new Set());
  const [dirtyRows, setDirtyRows] = useState(new Set());
  const [rowBuffers, setRowBuffers] = useState({});

  const [copiedCol, setCopiedCol] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fileInputRef = useRef(null);

  const handleDeleteRow = async (row) => {
    if (!isAdmin) {
      toast.error("Only administrators can delete RM items");
      return;
    }

    const rowName = row.RMName || row.RMCode || "this item";
    const confirmed = window.confirm(`Are you sure you want to delete ${rowName} from RM Database?`);
    if (!confirmed) return;

    // If it's a newly added unsaved row
    if (String(row.id).startsWith("new-") && !row._id) {
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setEditingRows((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      setDirtyRows((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      toast.success("Row removed");
      return;
    }

    try {
      setDeletingId(row.id);
      const targetId = row._id || row.id || row.RMCode;
      await deleteRMRow(targetId);

      setRows((prev) => prev.filter((r) => r.id !== row.id && r._id !== row._id));
      setEditingRows((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      setDirtyRows((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      toast.success("RM item deleted successfully");
    } catch (error) {
      console.error("Failed to delete RM row:", error);
      toast.error(error.response?.data?.message || "Failed to delete RM item");
    } finally {
      setDeletingId(null);
    }
  };

  const isFieldEditable = (row, key) => {
    const isNew = String(row.id).startsWith("new-") || !row._id;
    if (isNew) return true;
    return key === "categoryPremiumKgCad" || key === "allergenPremiumKgCad";
  };

  const handleExportExcel = () => {
    try {
      const dataToExport = filteredRows.map((row) => ({
        "RM Name": row.RMName,
        "RM Code": row.RMCode,
        "RM Category": row.RMCategory,
        "Category Premium / KG (CAD)": row.categoryPremiumKgCad,
        "Allergen Premium / KG (CAD)": row.allergenPremiumKgCad,
        "Base Cost / KG (CAD)": row.baseCostKgCad,
        "Total RM Cost": row.TotalRMCost,
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "RM Database");
      XLSX.writeFile(workbook, `RM_Database_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to export Excel file: " + error.message);
    }
  };

  const handleImportExcel = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        setLoading(true);
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet);

        for (const item of json) {
          const rawName = item["RMName"] || item["RM Name"] || item["Name"] || "";
          const rawCode = Number(item["RMCode"] || item["RM Code"] || item["Code"] || 0);
          const rawCategory = item["RMCategory"] || item["RM Category"] || item["Category"] || "";
          const rawBaseCost = Number(item["baseCostKgCad"] || item["Base Cost / KG (CAD)"] || item["Base Cost"] || 0);

          if (!rawName || !rawCode) continue;

          const premiums = getPremiumsByCategory(rawCategory);
          const categoryPremium = Number(
            item["categoryPremiumKgCad"] !== undefined
              ? item["categoryPremiumKgCad"]
              : (item["Category Premium / KG (CAD)"] !== undefined
                ? item["Category Premium / KG (CAD)"]
                : premiums.categoryPremiumKgCad)
          );
          const allergenPremium = Number(
            item["allergenPremiumKgCad"] !== undefined
              ? item["allergenPremiumKgCad"]
              : (item["Allergen Premium / KG (CAD)"] !== undefined
                ? item["Allergen Premium / KG (CAD)"]
                : premiums.allergenPremiumKgCad)
          );
          const totalCost = rawBaseCost + categoryPremium + allergenPremium;

          const payload = {
            RMName: rawName,
            RMCode: rawCode,
            RMCategory: rawCategory,
            baseCostKgCad: rawBaseCost,
            categoryPremiumKgCad: categoryPremium,
            allergenPremiumKgCad: allergenPremium,
            TotalRMCost: totalCost,
          };

          const existing = rows.find((r) => Number(r.RMCode) === rawCode);
          if (existing && existing._id) {
            await updateRMRow(existing._id, payload);
          } else {
            await createRMRow(payload);
          }
        }

        alert("Import completed successfully!");
        await fetchRows();
      } catch (error) {
        console.error("Import error:", error);
        alert("Failed to import Excel file: " + error.message);
      } finally {
        setLoading(false);
        e.target.value = "";
      }
    };
    reader.readAsArrayBuffer(file);
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const calculateTotalRMCost = (row) => {
    return (
      Number(row.baseCostKgCad || 0) +
      Number(row.categoryPremiumKgCad || 0) +
      Number(row.allergenPremiumKgCad || 0)
    );
  };

  const fetchRows = async () => {
    try {
      setLoading(true);

      const data = await getRMRows();

      // const formatted = (data || []).map((item, index) => ({
      //   id: item._id || item.id || `${Date.now()}-${index}`,
      //   _id: item._id || null,

      //   RMName: item.RMName || "",
      //   RMCode: Number(item.RMCode || 0),
      //   RMCategory: item.RMCategory || "",

      //   baseCostKgCad: Number(item.baseCostKgCad || 0),
      //   categoryPremiumKgCad: Number(item.categoryPremiumKgCad || 0),
      //   allergenPremiumKgCad: Number(item.allergenPremiumKgCad || 0),
      //   TotalRMCost:
      //     item.TotalRMCost !== undefined
      //       ? Number(item.TotalRMCost || 0)
      //       : Number(item.baseCostKgCad || 0) +
      //       Number(item.categoryPremiumKgCad || 0) +
      //       Number(item.allergenPremiumKgCad || 0),
      // }));

      const formatted = (data || []).map((item, index) => {
        const premiums = getPremiumsByCategory(item.RMCategory);

        return {
          id: item._id || item.id || `${Date.now()}-${index}`,
          _id: item._id || null,

          RMName: item.RMName || "",
          RMCode: Number(item.RMCode || 0),
          RMCategory: item.RMCategory || "",

          baseCostKgCad: Number(item.baseCostKgCad || 0),

          categoryPremiumKgCad:
            item.categoryPremiumKgCad !== undefined && item.categoryPremiumKgCad !== null
              ? Number(item.categoryPremiumKgCad)
              : Number(premiums.categoryPremiumKgCad || 0),

          allergenPremiumKgCad:
            item.allergenPremiumKgCad !== undefined && item.allergenPremiumKgCad !== null
              ? Number(item.allergenPremiumKgCad)
              : Number(premiums.allergenPremiumKgCad || 0),

          TotalRMCost:
            item.TotalRMCost !== undefined && item.TotalRMCost !== null
              ? Number(item.TotalRMCost)
              : Number(item.baseCostKgCad || 0) +
                Number(
                  item.categoryPremiumKgCad !== undefined && item.categoryPremiumKgCad !== null
                    ? item.categoryPremiumKgCad
                    : premiums.categoryPremiumKgCad || 0
                ) +
                Number(
                  item.allergenPremiumKgCad !== undefined && item.allergenPremiumKgCad !== null
                    ? item.allergenPremiumKgCad
                    : premiums.allergenPremiumKgCad || 0
                ),
        };
      });
      setRows(formatted);
      setCurrentPage(1);
    } catch (error) {
      console.log("Fetch RM Rows Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    let result = rows;

    if (categoryFilter) {
      result = result.filter(
        (row) => String(row.RMCategory || "").trim().toLowerCase() === categoryFilter.trim().toLowerCase()
      );
    }

    const q = search.toLowerCase().trim();
    if (q) {
      result = result.filter((row) => {
        return (
          String(row.RMName || "").toLowerCase().includes(q) ||
          String(row.RMCode || "").toLowerCase().includes(q) ||
          String(row.RMCategory || "").toLowerCase().includes(q)
        );
      });
    }

    return result;
  }, [rows, search, categoryFilter]);

  const totalRows = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const paginatedRows = useMemo(() => {
    return filteredRows.slice(startIndex, endIndex);
  }, [filteredRows, startIndex, endIndex]);

  const startRowNumber = totalRows === 0 ? 0 : startIndex + 1;
  const endRowNumber = Math.min(endIndex, totalRows);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const isAllEditing =
    rows.length > 0 && rows.every((row) => editingRows.has(row.id));
  const isAnyEditing = editingRows.size > 0;

  const getVal = (row, field) => {
    if (editingRows.has(row.id) && rowBuffers[row.id]) {
      return rowBuffers[row.id][field];
    }

    return row[field];
  };

  const toggleEditAll = () => {
    if (isAllEditing) {
      setEditingRows(new Set());
      setRowBuffers({});
      setDirtyRows(new Set());
    } else {
      const buffers = {};

      rows.forEach((row) => {
        buffers[row.id] = { ...row };
      });

      setEditingRows(new Set(rows.map((row) => row.id)));
      setRowBuffers(buffers);
    }
  };

  const toggleEditRow = (row) => {
    const id = row.id;

    if (editingRows.has(id)) {
      setEditingRows((prev) => {
        const updated = new Set(prev);
        updated.delete(id);
        return updated;
      });

      setRowBuffers((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });

      setDirtyRows((prev) => {
        const updated = new Set(prev);
        updated.delete(id);
        return updated;
      });
    } else {
      setEditingRows((prev) => new Set([...prev, id]));

      setRowBuffers((prev) => ({
        ...prev,
        [id]: { ...row },
      }));
    }
  };

  const updateBuffer = (id, field, value) => {
    setRowBuffers((prev) => {
      const current = {
        ...(prev[id] || rows.find((row) => row.id === id) || {}),
        [field]: value,
      };

      if (
        field === "baseCostKgCad" ||
        field === "categoryPremiumKgCad" ||
        field === "allergenPremiumKgCad"
      ) {
        current.TotalRMCost = calculateTotalRMCost(current);
      }

      return {
        ...prev,
        [id]: current,
      };
    });

    setDirtyRows((prev) => new Set([...prev, id]));
  };

  const copyColumn = async (field) => {
    const text = filteredRows.map((row) => getVal(row, field) ?? "").join("\n");

    try {
      await navigator.clipboard.writeText(text);
      setCopiedCol(field);

      setTimeout(() => {
        setCopiedCol(null);
      }, 2000);
    } catch (error) {
      alert("Copy failed");
    }
  };

  const handlePaste = (e, rowId, field) => {
    const text = e.clipboardData.getData("text");

    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length <= 1) return;

    e.preventDefault();

    const startIdx = rows.findIndex((row) => row.id === rowId);
    if (startIdx === -1) return;

    const col = COLUMNS.find((c) => c.key === field);
    const newBuffers = { ...rowBuffers };
    const newDirty = new Set(dirtyRows);
    const newEditing = new Set(editingRows);

    lines.forEach((val, i) => {
      const idx = startIdx + i;

      if (idx < rows.length) {
        const id = rows[idx].id;

        newEditing.add(id);

        const updatedRow = {
          ...(newBuffers[id] || rows[idx]),
          [field]: col?.type === "number" ? Number(val || 0) : val,
        };

        if (
          field === "baseCostKgCad" ||
          field === "categoryPremiumKgCad" ||
          field === "allergenPremiumKgCad"
        ) {
          updatedRow.TotalRMCost = calculateTotalRMCost(updatedRow);
        }

        newBuffers[id] = updatedRow;
        newDirty.add(id);
      }
    });

    setEditingRows(newEditing);
    setRowBuffers(newBuffers);
    setDirtyRows(newDirty);
  };

  const buildPayload = (row) => {
    const total =
      row.TotalRMCost !== undefined
        ? Number(row.TotalRMCost || 0)
        : calculateTotalRMCost(row);

    return {
      RMName: row.RMName,
      RMCode: Number(row.RMCode || 0),
      RMCategory: row.RMCategory,
      baseCostKgCad: Number(row.baseCostKgCad || 0),
      categoryPremiumKgCad: Number(row.categoryPremiumKgCad || 0),
      allergenPremiumKgCad: Number(row.allergenPremiumKgCad || 0),
      TotalRMCost: total,
    };
  };

  const saveRow = async (id) => {
    try {
      const buffer = rowBuffers[id];
      if (!buffer) return;

      setSaving(true);

      let savedRow = {
        ...buffer,
        TotalRMCost:
          buffer.TotalRMCost !== undefined
            ? Number(buffer.TotalRMCost || 0)
            : calculateTotalRMCost(buffer),
      };

      if (buffer._id) {
        await updateRMRow(buffer._id, buildPayload(savedRow));
      } else {
        const created = await createRMRow(buildPayload(savedRow));

        savedRow = {
          ...savedRow,
          _id: created?._id,
          id: created?._id || savedRow.id,
        };
      }

      setRows((prev) => prev.map((row) => (row.id === id ? savedRow : row)));

      setEditingRows((prev) => {
        const updated = new Set(prev);
        updated.delete(id);
        return updated;
      });

      setRowBuffers((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });

      setDirtyRows((prev) => {
        const updated = new Set(prev);
        updated.delete(id);
        return updated;
      });
    } catch (error) {
      console.log("Save Row Error:", error);
    } finally {
      setSaving(false);
    }
  };

  const saveAll = async () => {
    try {
      setSaving(true);

      const updatedRows = rows.map((row) => {
        if (dirtyRows.has(row.id) && rowBuffers[row.id]) {
          const buffer = rowBuffers[row.id];

          return {
            ...buffer,
            TotalRMCost:
              buffer.TotalRMCost !== undefined
                ? Number(buffer.TotalRMCost || 0)
                : calculateTotalRMCost(buffer),
          };
        }

        return row;
      });

      await bulkSaveRMRows(
        updatedRows.map((row) => ({
          _id: row._id,
          ...buildPayload(row),
        }))
      );

      setRows(updatedRows);
      setEditingRows(new Set());
      setRowBuffers({});
      setDirtyRows(new Set());

      await fetchRows();
    } catch (error) {
      console.log("Save All Error:", error);
    } finally {
      setSaving(false);
    }
  };

  const addRow = () => {
    const newId = `new-${Date.now()}`;

    const newRow = {
      id: newId,
      _id: null,
      RMName: "",
      RMCode: 0,
      RMCategory: "",
      baseCostKgCad: 0,
      categoryPremiumKgCad: 0,
      allergenPremiumKgCad: 0,
      TotalRMCost: 0,
    };

    setRows((prev) => [newRow, ...prev]);
    setEditingRows((prev) => new Set([...prev, newId]));
    setRowBuffers((prev) => ({
      ...prev,
      [newId]: { ...newRow },
    }));
    setDirtyRows((prev) => new Set([...prev, newId]));
  };



  return (
    <div className="w-full font-sans mb-5">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div
          className="flex items-center justify-between px-6 py-5 cursor-pointer bg-gradient-to-r from-gray-50 to-white"
          onClick={() => setOpen((prev) => !prev)}
        >
          <div>
            <h2 className="text-2xl font-bold text-gray-900">RM Database</h2>

            {/* <p className="text-sm text-gray-500 mt-1">
              Raw material costs and premiums per KG
            </p> */}
          </div>

          <button
            type="button"
            className="flex items-center justify-center  border-gray-300 hover:bg-gray-100 text-xl text-gray-600 transition"
          >
            {open ? "-" : "+"}
          </button>
        </div>

        {open && (
          <div className="border-t border-gray-100 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative w-72">
                  <Search
                    size={16}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    placeholder="Search RM Name / Code / Category..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full bg-gray-50/50 hover:bg-white border border-gray-200 focus:border-black rounded-xl pl-10 pr-4 py-2.5 text-sm transition focus:outline-none focus:ring-1 focus:ring-black placeholder-gray-400 shadow-sm"
                  />
                </div>

                <div className="relative w-60">
                  <Filter
                    size={15}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <select
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full bg-gray-50/50 hover:bg-white border border-gray-200 focus:border-black rounded-xl pl-9 pr-9 py-2.5 text-sm transition focus:outline-none focus:ring-1 focus:ring-black appearance-none cursor-pointer text-gray-800 shadow-sm font-medium"
                  >
                    <option value="">All Categories</option>
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={fetchRows}
                  disabled={loading}
                  title="Refresh Database"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition active:scale-95 disabled:opacity-50"
                >
                  <RefreshCcw
                    size={14}
                    className={loading ? "animate-spin" : ""}
                  />
                  <span>Refresh</span>
                </button>

                <button
                  type="button"
                  onClick={addRow}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition active:scale-95"
                >
                  <Plus size={15} className="text-gray-600" />
                  <span>Add Row</span>
                </button>

                <button
                  type="button"
                  onClick={handleExportExcel}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition active:scale-95"
                >
                  <Download size={14} className="text-gray-600" />
                  <span>Export</span>
                </button>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition active:scale-95"
                >
                  <Upload size={14} className="text-gray-600" />
                  <span>Import</span>
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImportExcel}
                  accept=".xlsx,.xls"
                  className="hidden"
                />

                {dirtyRows.size > 0 && (
                  <button
                    type="button"
                    onClick={saveAll}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow-sm transition active:scale-95"
                  >
                    <Check size={14} />
                    <span>{saving ? "Saving..." : "Save All"}</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={toggleEditAll}
                  className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition active:scale-95 ${
                    isAnyEditing
                      ? "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                      : "bg-black text-white hover:bg-gray-800"
                  }`}
                >
                  {isAnyEditing ? (
                    <>
                      <X size={14} className="text-gray-600" />
                      <span>Cancel Edit</span>
                    </>
                  ) : (
                    <>
                      <Pencil size={13} />
                      <span>Edit All</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 rounded-2xl">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="px-3 py-3.5 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-10">
                      #
                    </th>

                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className={`px-3 py-3.5 text-xs font-bold text-gray-500 uppercase tracking-wider ${
                          col.headerAlign || (col.type === "number" && col.key !== "RMCode" ? "text-right" : "text-left")
                        }`}
                      >
                        <div
                          className={`inline-flex items-center gap-1.5 ${
                            col.type === "number" && col.key !== "RMCode" ? "justify-end" : "justify-start"
                          }`}
                        >
                          <span className="whitespace-nowrap">
                            {col.label}
                          </span>

                          <button
                            type="button"
                            onClick={() => copyColumn(col.key)}
                            className="p-1 rounded hover:bg-gray-200 transition"
                          >
                            {copiedCol === col.key ? (
                              <Check size={12} className="text-green-600" />
                            ) : (
                              <Copy size={12} className="text-gray-400" />
                            )}
                          </button>
                        </div>
                      </th>
                    ))}

                    <th className="px-3 py-3.5 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-20 whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100 bg-white">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={COLUMNS.length + 2}
                        className="py-16 text-center text-gray-500"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : paginatedRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={COLUMNS.length + 2}
                        className="py-16 text-center text-gray-500"
                      >
                        No Data Found
                      </td>
                    </tr>
                  ) : (
                    paginatedRows.map((row, idx) => {
                      const isEditing = editingRows.has(row.id);
                      const isDirty = dirtyRows.has(row.id);

                      return (
                        <tr
                          key={row.id}
                          className={`transition hover:bg-gray-50/80 ${isEditing ? "bg-gray-50" : ""
                            }`}
                        >
                          <td className="px-3 py-3 text-gray-400 text-xs font-mono">
                            {startIndex + idx + 1}
                          </td>

                          {COLUMNS.map((col) => (
                            <td
                              key={col.key}
                              className={`px-3 py-3 ${
                                col.type === "number" && col.key !== "RMCode"
                                  ? "text-right whitespace-nowrap"
                                  : col.key === "RMName"
                                  ? "text-left"
                                  : "text-left whitespace-nowrap"
                              }`}
                            >
                              {isEditing && isFieldEditable(row, col.key) ? (
                                col.key === "RMCategory" ? (
                                  <select
                                    value={getVal(row, col.key)}
                                    onChange={(e) =>
                                      updateBuffer(row.id, col.key, e.target.value)
                                    }
                                    className="w-full px-2 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black bg-white font-sans"
                                  >
                                    <option value="">Select Category</option>
                                    {CATEGORIES.map((cat) => (
                                      <option key={cat} value={cat}>
                                        {cat}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <InlineInput
                                    type={col.type}
                                    value={getVal(row, col.key)}
                                    onChange={(value) =>
                                      updateBuffer(row.id, col.key, value)
                                    }
                                    onPaste={(e) =>
                                      handlePaste(e, row.id, col.key)
                                    }
                                  />
                                )
                              ) : (
                                <span
                                  className={
                                    col.type === "number" && col.key !== "RMCode"
                                      ? "font-mono text-gray-800 font-semibold"
                                      : col.key === "RMCode"
                                        ? "font-mono text-gray-600 text-xs sm:text-sm"
                                        : col.key === "RMName"
                                          ? "font-sans text-gray-900 font-medium text-sm leading-snug block max-w-xl"
                                          : "font-sans text-gray-700 text-sm"
                                  }
                                >
                                  {col.type === "number" && col.key !== "RMCode" ? "$" : ""}
                                  {fmt(getVal(row, col.key), col.type, col.key)}
                                </span>
                              )}
                            </td>
                          ))}

                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              {!isEditing ? (
                                <button
                                  type="button"
                                  onClick={() => toggleEditRow(row)}
                                  title="Edit Row"
                                  className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition"
                                >
                                  <Pencil size={13} />
                                </button>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => saveRow(row.id)}
                                    disabled={saving}
                                    title="Save Row"
                                    className="p-2 rounded-lg bg-black text-white hover:opacity-90 transition disabled:opacity-50"
                                  >
                                    <Check size={13} />
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => toggleEditRow(row)}
                                    title="Cancel Edit"
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 text-gray-600 transition"
                                  >
                                    <X size={13} />
                                  </button>
                                </>
                              )}

                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteRow(row)}
                                  disabled={deletingId === row.id}
                                  title="Delete RM Item (Admin Only)"
                                  className="p-2 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:text-red-700 transition disabled:opacity-50"
                                >
                                  <Trash2 size={13} className={deletingId === row.id ? "animate-spin" : ""} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-6">
              <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                <span>
                  Showing{" "}
                  <span className="font-semibold text-gray-800">
                    {startRowNumber}
                  </span>
                  –
                  <span className="font-semibold text-gray-800">
                    {endRowNumber}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-black">{totalRows}</span>
                </span>

                <div className="flex items-center gap-2">
                  <span>Per page:</span>

                  <select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-sm focus:outline-none"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

             <div className="flex items-center gap-2 flex-wrap">
  {/* First Page */}
  <button
    type="button"
    onClick={() => goToPage(1)}
    disabled={currentPage === 1}
    className="px-3 py-2 border border-gray-300 rounded-xl text-sm
      text-gray-700 bg-white
      disabled:opacity-40 disabled:cursor-not-allowed
      hover:bg-gray-50 transition"
  >
    First
  </button>

  {/* Previous */}
  <button
    type="button"
    onClick={() => goToPage(currentPage - 1)}
    disabled={currentPage === 1}
    className="px-3 py-2 border border-gray-300 rounded-xl text-sm
      text-gray-700 bg-white
      disabled:opacity-40 disabled:cursor-not-allowed
      hover:bg-gray-50
      flex items-center gap-1 transition"
  >
    <ChevronLeft size={14} />
    Prev
  </button>

  {/* Page Numbers */}
  {Array.from({ length: totalPages }, (_, i) => i + 1)
    .slice(
      Math.max(currentPage - 3, 0),
      Math.min(currentPage + 2, totalPages)
    )
    .map((page) => (
      <button
        type="button"
        key={page}
        onClick={() => goToPage(page)}
        className={`w-9 h-9 rounded-xl text-sm border transition
          ${
            currentPage === page
              ? "bg-black text-white border-black"
              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
          }`}
      >
        {page}
      </button>
    ))}

  {/* Next */}
  <button
    type="button"
    onClick={() => goToPage(currentPage + 1)}
    disabled={currentPage === totalPages}
    className="px-3 py-2 border border-gray-300 rounded-xl text-sm
      text-gray-700 bg-white
      disabled:opacity-40 disabled:cursor-not-allowed
      hover:bg-gray-50
      flex items-center gap-1 transition"
  >
    Next
    <ChevronRight size={14} />
  </button>

  {/* Last Page */}
  <button
    type="button"
    onClick={() => goToPage(totalPages)}
    disabled={currentPage === totalPages}
    className="px-3 py-2 border border-gray-300 rounded-xl text-sm
      text-gray-700 bg-white
      disabled:opacity-40 disabled:cursor-not-allowed
      hover:bg-gray-50 transition"
  >
    Last
  </button>
</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}